import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  company_name: "",
  email: "",
  phone: "",
  volume: "",
  description: "",
};

export default function PickupFormCard() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.volume) {
      toast.error("Please select an estimated volume.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pickup-requests`, form);
      toast.success("Pickup request received", {
        description: "Our logistics team will get back to you within 24 hours.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Could not submit your request", {
        description: typeof detail === "string" ? detail : "Please check your details and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_32px_80px_rgba(4,10,7,0.10)] sm:p-10"
      data-testid="booking-form"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="full-name" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Full Name
          </Label>
          <Input
            id="full-name"
            data-testid="booking-full-name-input"
            placeholder="Jane Citizen"
            value={form.full_name}
            onChange={set("full_name")}
            required
            className="h-12 rounded-xl border-slate-200 bg-slate-50 transition-[border-color,box-shadow] focus-visible:ring-eco"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="company-name" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Company Name
          </Label>
          <Input
            id="company-name"
            data-testid="booking-company-name-input"
            placeholder="Acme Corp Pty Ltd"
            value={form.company_name}
            onChange={set("company_name")}
            required
            className="h-12 rounded-xl border-slate-200 bg-slate-50 transition-[border-color,box-shadow] focus-visible:ring-eco"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="booking-email-input"
            placeholder="jane@acme.com.au"
            value={form.email}
            onChange={set("email")}
            required
            className="h-12 rounded-xl border-slate-200 bg-slate-50 transition-[border-color,box-shadow] focus-visible:ring-eco"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Phone Number
          </Label>
          <Input
            id="phone"
            type="tel"
            data-testid="booking-phone-input"
            placeholder="+61 4XX XXX XXX"
            value={form.phone}
            onChange={set("phone")}
            required
            className="h-12 rounded-xl border-slate-200 bg-slate-50 transition-[border-color,box-shadow] focus-visible:ring-eco"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Estimated Volume
          </Label>
          <Select
            value={form.volume}
            onValueChange={(v) => setForm((f) => ({ ...f, volume: v }))}
          >
            <SelectTrigger
              data-testid="booking-volume-select"
              className="h-12 rounded-xl border-slate-200 bg-slate-50 focus:ring-eco"
            >
              <SelectValue placeholder="How many items are we collecting?" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1-10 items" data-testid="volume-option-1-10">1-10 items</SelectItem>
              <SelectItem value="11-50 items" data-testid="volume-option-11-50">11-50 items</SelectItem>
              <SelectItem value="50+ items" data-testid="volume-option-50-plus">50+ items</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="description" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
            Equipment Details
          </Label>
          <Textarea
            id="description"
            data-testid="booking-description-textarea"
            placeholder="Briefly describe the equipment (e.g., 20 Dell laptops, 3 servers)"
            value={form.description}
            onChange={set("description")}
            rows={4}
            className="rounded-xl border-slate-200 bg-slate-50 transition-[border-color,box-shadow] focus-visible:ring-eco"
          />
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={submitting}
        whileHover={{ scale: submitting ? 1 : 1.02 }}
        whileTap={{ scale: submitting ? 1 : 0.98 }}
        data-testid="booking-submit-button"
        className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white shadow-[0_12px_32px_rgba(4,10,7,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-eco hover:text-navy hover:shadow-[0_12px_40px_rgba(52,199,123,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco disabled:cursor-not-allowed disabled:opacity-70"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending request…
          </>
        ) : (
          <>
            Request Pickup Estimate
            <Send className="h-4 w-4" />
          </>
        )}
      </motion.button>
      <p className="mt-4 text-center text-xs text-slate-500" data-testid="booking-privacy-note">
        Your details are only used to arrange your collection. No spam, ever.
      </p>
    </form>
  );
}
